import { translate } from "@/plugins/i18n";
import type BaseEnumType from "../../baseEnumType";
import Key from "@/app/types/common/key";
import { Maybe } from "@/components/utils/typeHelpers";


export class EnumMetricType implements BaseEnumType {
  private static allValues: EnumMetricType[] = []; // make sure this is top-most
  
  public static custom = new EnumMetricType(0, "Custom", "enum_metric_type_custom");
  public static customBoolean = new EnumMetricType(1, "CustomBoolean", "enum_metric_type_custom_boolean");
  public static customString = new EnumMetricType(2, "CustomString", "enum_metric_type_custom_string");
  public static temperatureCelsius = new EnumMetricType(3, "TemperatureCelsius", "enum_metric_type_temperature_celsius");
  public static temperatureFahrenheit = new EnumMetricType(4, "TemperatureFahrenheit", "enum_metric_type_temperature_fahrenheit");
  public static temperatureKelvin = new EnumMetricType(5, "TemperatureKelvin", "enum_metric_type_temperature_kelvin");
  public static gravityRelativeToWater = new EnumMetricType(6, "GravityRelativeToWater", "enum_metric_type_gravity_relative_to_water");
  public static gravityKgm3 = new EnumMetricType(7, "GravityKgm3", "enum_metric_type_gravity_kgm3");
  public static gravityKgm3ExcessOfWater = new EnumMetricType(8, "GravityKgm3ExcessOfWater", "enum_metric_type_gravity_kgm3_excess_of_water");
  public static gravityPlato = new EnumMetricType(9, "GravityPlato", "enum_metric_type_gravity_plato");
  public static ph = new EnumMetricType(10, "Ph", "enum_metric_type_ph");
  public static abv = new EnumMetricType(11, "Abv", "enum_metric_type_abv");
  
  public key: number;
  public value: string;
  public translationKey: string;

  private constructor(key: number, value: string, translationKey: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumMetricType.allValues.push(this);
  }

  public get translation() { return translate(Key.fromString(this.translationKey)); }

  public isCustom() { return this === EnumMetricType.custom; }
  public isCustomBoolean() { return this === EnumMetricType.customBoolean; }
  public isCustomString() { return this === EnumMetricType.customString; }
  public isTemperatureCelsius() { return this === EnumMetricType.temperatureCelsius; }
  public isTemperatureFahrenheit() { return this === EnumMetricType.temperatureFahrenheit; }
  public isTemperatureKelvin() { return this === EnumMetricType.temperatureKelvin; }
  public isGravityRelativeToWater() { return this === EnumMetricType.gravityRelativeToWater; }
  public isGravityKgm3() { return this === EnumMetricType.gravityKgm3; }
  public isGravityKgm3ExcessOfWater() { return this === EnumMetricType.gravityKgm3ExcessOfWater; }
  public isGravityPlato() { return this === EnumMetricType.gravityPlato; }
  public isPh() { return this === EnumMetricType.ph; }
  public isAbv() { return this === EnumMetricType.abv; }

  public static getValues(): readonly EnumMetricType[] {
    return EnumMetricType.allValues;
  }

  public static getValuesExcept0(): readonly EnumMetricType[] {
    return EnumMetricType.allValues.filter((x) => x.key !== 0);
  }

  public static getByKey(key: number): EnumMetricType {
    const found = EnumMetricType.allValues.find(e => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumMetricType - Key not found: ${ key }`);
    }

    return found;
  }

  public static getByValue(name: string, def?: EnumMetricType): EnumMetricType {
    const found = name ? EnumMetricType.allValues.find(e => e.value.toLowerCase() === name.toLowerCase()) : null;
    if (!found) {
      if (def) {
        return def;
      }
      throw new Error(`EnumMetricType - Value not found: ${ name }`);
    }

    return found;
  }

  public equals(other: Maybe<EnumMetricType>): boolean {
    return this.key === other?.key;
  }
}